'use client'

import { useCallback, useEffect, useState } from 'react'
import { consentManager } from '@/lib/consent-manager'

interface RecaptchaState {
  isLoaded: boolean
  isLoading: boolean
  error: string | null
  consentGiven: boolean
  consentSkipped: boolean
}

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
    }
  }
}

export function useRecaptcha(siteKey: string) {
  const [state, setState] = useState<RecaptchaState>({
    isLoaded: false,
    isLoading: false,
    error: null,
    consentGiven: false,
    consentSkipped: false,
  })

  useEffect(() => {
    // Check consent status for analytics (reCAPTCHA uses third-party cookies)
    const hasAnalyticsConsent = consentManager.hasConsent('analytics')

    // If no consent given, mark as skipped (form can still work without reCAPTCHA)
    if (!hasAnalyticsConsent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reads consent manager singleton, must run client-only after mount
      setState((prev) => ({
        ...prev,
        consentSkipped: true,
        isLoaded: true, // Mark as "loaded" so form isn't blocked
      }))
      return
    }

    setState((prev) => ({ ...prev, consentGiven: true }))

    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha) {
      setState((prev) => ({ ...prev, isLoaded: true }))
      return
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="recaptcha"]')
    if (existingScript) {
      setState((prev) => ({ ...prev, isLoading: true }))

      const handleLoad = () => {
        setState((prev) => ({ ...prev, isLoaded: true, isLoading: false }))
      }

      const handleError = () => {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load reCAPTCHA script',
        }))
      }

      existingScript.addEventListener('load', handleLoad)
      existingScript.addEventListener('error', handleError)

      return () => {
        existingScript.removeEventListener('load', handleLoad)
        existingScript.removeEventListener('error', handleError)
      }
    }

    // Load reCAPTCHA lazily on first form interaction, so its third-party
    // cookies are not set for visitors who never use the form
    const loadScript = () => {
      setState((prev) => ({ ...prev, isLoading: true }))

      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
      script.async = true
      script.defer = true

      const handleLoad = () => {
        setState((prev) => ({ ...prev, isLoaded: true, isLoading: false }))
      }

      const handleError = () => {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load reCAPTCHA script',
        }))
      }

      script.addEventListener('load', handleLoad)
      script.addEventListener('error', handleError)

      document.head.appendChild(script)
    }

    const onFirstInteraction = (event: Event) => {
      if (!(event.target as Element | null)?.closest?.('form')) return
      document.removeEventListener('focusin', onFirstInteraction)
      loadScript()
    }
    document.addEventListener('focusin', onFirstInteraction)

    return () => {
      document.removeEventListener('focusin', onFirstInteraction)
    }
  }, [siteKey])

  // Listen for consent changes
  useEffect(() => {
    return consentManager.subscribe((consent) => {
      if (consent.analytics && state.consentSkipped) {
        // User just gave consent, reload to enable reCAPTCHA
        setState((prev) => ({
          ...prev,
          consentSkipped: false,
          consentGiven: true,
          isLoaded: false,
          isLoading: true,
        }))

        // Load the script now
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`
        script.async = true
        script.defer = true
        script.onload = () => {
          setState((prev) => ({ ...prev, isLoaded: true, isLoading: false }))
        }
        script.onerror = () => {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Failed to load reCAPTCHA script',
          }))
        }
        document.head.appendChild(script)
      }
    })
  }, [siteKey, state.consentSkipped])

  const executeRecaptcha = useCallback(
    async (action: string = 'submit'): Promise<string | null> => {
      // If consent was skipped, return special token indicating no reCAPTCHA
      if (state.consentSkipped) {
        return 'consent_skipped'
      }

      if (!state.isLoaded || !window.grecaptcha) {
        setState((prev) => ({ ...prev, error: 'reCAPTCHA not loaded' }))
        return null
      }

      try {
        setState((prev) => ({ ...prev, error: null }))

        return new Promise((resolve) => {
          window.grecaptcha.ready(async () => {
            try {
              const token = await window.grecaptcha.execute(siteKey, { action })
              resolve(token)
            } catch (error) {
              console.error('reCAPTCHA execution error:', error)
              setState((prev) => ({
                ...prev,
                error: 'Failed to execute reCAPTCHA',
              }))
              resolve(null)
            }
          })
        })
      } catch (error) {
        console.error('reCAPTCHA execution error:', error)
        setState((prev) => ({ ...prev, error: 'Failed to execute reCAPTCHA' }))
        return null
      }
    },
    [siteKey, state.isLoaded, state.consentSkipped]
  )

  return {
    ...state,
    executeRecaptcha,
  }
}
